import {
  type ReactNode,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
} from "react";

import { useTranslation } from "react-i18next";
import { useBlocker } from "react-router";

import { CloudCheckIcon, type LucideIcon, SaveIcon } from "lucide-react";

import { styled } from "simplydo/react/styled";
import { Icon } from "simplydo/react/ui/icon";

import {
  EntityToolbar,
  type ToolbarItem,
  ToolbarItems,
} from "components/lib/EntityHeader";

import { UnsavedChangesModal } from "./UnsavedChangesModal";
import type { EditChange } from "./changes";
import { useEditToolbarStore } from "./store";

export type EditToolbarAction = {
  key: string;
  label: ReactNode;
  icon?: LucideIcon;
  onClick: () => void;
  disabled?: boolean;
  tooltip?: ReactNode;
  /** Always listed under "More", e.g. destructive actions. */
  overflow?: boolean;
  /** How long it stays in the toolbar as space runs out. See `ToolbarItem`. */
  tier?: "high" | "default" | "low";
};

type EditToolbarProps = {
  /** There are edits that need saving with `onSave`. */
  changed?: boolean;
  /** What's unsaved, in words, for the dialog shown before leaving. See `./changes.ts`. */
  changes?: EditChange[];
  saving?: boolean;
  onSave?: () => void;
  saveLabel?: ReactNode;
  saveDisabled?: boolean;
  /** Puts back the last saved values. */
  onDiscard?: () => void;
  /** Edits on this page save as they are made. */
  autosave?: boolean;
  /** Asks before leaving the page. Defaults to having unsaved edits. */
  blockLeave?: boolean;
  actions?: EditToolbarAction[];
  /** Page-specific controls that aren't actions, e.g. a search field. Hidden first when space runs out. */
  tools?: ReactNode;
};

const Status = styled.span`
  display: inline-flex;
  align-items: center;
  color: rgba(0, 0, 0, 0.6);
  font-size: 13px;
  white-space: nowrap;
`;

/**
 * The controls for what the current page edits: its status, actions, and
 * Save and Discard. They join the area's toolbar (`EditToolbarItems`), or
 * show in a toolbar of their own where the page has none.
 */
export function EditToolbar({
  changed = false,
  changes = [],
  saving = false,
  onSave,
  saveLabel,
  saveDisabled,
  onDiscard,
  autosave = false,
  blockLeave = changed && !!onSave,
  actions = [],
  tools,
}: EditToolbarProps) {
  const { t } = useTranslation();
  const id = useId();
  const hosted = useEditToolbarStore((state) => state.hosts > 0);

  useLayoutEffect(() => {
    useEditToolbarStore.setState((state) => ({ active: state.active + 1 }));
    return () =>
      useEditToolbarStore.setState((state) => ({ active: state.active - 1 }));
  }, []);

  // Switching tabs within a page changes only the query, so it doesn't count as leaving.
  const blocker = useBlocker(
    ({ currentLocation, nextLocation }) =>
      blockLeave && currentLocation.pathname !== nextLocation.pathname,
  );
  const [saveAndLeave, setSaveAndLeave] = useState(false);
  const wasSaving = useRef(false);
  useEffect(() => {
    if (!saveAndLeave || blocker.state !== "blocked") return;
    if (!changed) {
      setSaveAndLeave(false);
      blocker.proceed();
    } else if (wasSaving.current && !saving) {
      // Saving failed: stay, so the error can be fixed.
      setSaveAndLeave(false);
      blocker.reset();
    }
    wasSaving.current = saving;
  }, [saveAndLeave, blocker, changed, saving]);

  useEffect(() => {
    if (!blockLeave) return;
    const warn = (e: BeforeUnloadEvent) => e.preventDefault();
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [blockLeave]);

  const items: ToolbarItem[] = [
    ...(tools
      ? [
          {
            key: "edit-tools",
            group: "edit-tools",
            tier: "low" as const,
            label: null,
            render: () => tools,
            menu: false as const,
          },
        ]
      : []),
    ...actions.map((action): ToolbarItem => ({
      key: `edit-${action.key}`,
      group: "edit-tools",
      tier: action.overflow ? "menu" : (action.tier ?? "default"),
      label: action.label,
      icon: action.icon,
      onClick: action.onClick,
      disabled: action.disabled,
      tooltip: action.tooltip,
    })),
    ...(autosave && !onSave
      ? [
          {
            key: "edit-autosave",
            group: "edit-save",
            tier: "low" as const,
            label: t("generic.editToolbar.autosave"),
            render: () => (
              <Status role="status">
                <Icon icon={CloudCheckIcon} />
                {t("generic.editToolbar.autosave")}
              </Status>
            ),
            menu: false as const,
          },
        ]
      : []),
    ...(onDiscard
      ? [
          {
            key: "edit-discard",
            group: "edit-save",
            tier: "pinned" as const,
            label: t("generic.discard"),
            disabled: !changed || saving,
            onClick: onDiscard,
          },
        ]
      : []),
    ...(onSave
      ? [
          {
            key: "edit-save",
            group: "edit-save",
            tier: "pinned" as const,
            label: saveLabel ?? t("generic.save"),
            icon: SaveIcon,
            variant: "regular" as const,
            color: "primary",
            disabled: !changed || saveDisabled,
            loading: saving,
            onClick: onSave,
          },
        ]
      : []),
  ];

  useLayoutEffect(() => {
    if (!hosted) return;
    useEditToolbarStore.setState((state) => ({
      items: { ...state.items, [id]: items },
    }));
  });
  useLayoutEffect(
    () => () =>
      useEditToolbarStore.setState((state) => {
        const remaining = { ...state.items };
        delete remaining[id];
        return { items: remaining };
      }),
    [id],
  );

  const unsavedChangesModal = (
    <UnsavedChangesModal
      open={blocker.state === "blocked"}
      changes={changes}
      saving={saveAndLeave}
      saveLabel={saveLabel}
      saveDisabled={saveDisabled}
      onSave={
        onSave
          ? () => {
              wasSaving.current = false;
              setSaveAndLeave(true);
              onSave();
            }
          : undefined
      }
      onDiscard={() => {
        onDiscard?.();
        blocker.proceed?.();
      }}
      onContinue={() => {
        setSaveAndLeave(false);
        blocker.reset?.();
      }}
    />
  );

  return (
    <>
      {hosted ? null : <EntityToolbar end={<ToolbarItems items={items} />} />}
      {unsavedChangesModal}
    </>
  );
}
